// LoginPage.jsx - Supabase Auth Login for RLS-enabled app
// NOTE: /reset-password route will be implemented later.
// Supabase will still process the recovery token correctly for now.
import React, { useState } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../supabaseClient';
import { Eye, EyeOff } from 'lucide-react';
import BrioMark from '../../components/BrioMark';

// Simple email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state (separate from login state)
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);
  const [resetError, setResetError] = useState(null);

  // Sign in with email + password
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        return;
      }

      console.log('✅ Login successful:', data.user?.email);
      if (onLoginSuccess) onLoginSuccess(data.session);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Send magic link (passwordless)
  const handleMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (otpError) {
        setError(otpError.message);
        return;
      }

      setMagicLinkSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  // Handle showing forgot password UI
  const handleShowReset = () => {
    setShowReset(true);
    setResetEmail(email); // Default to current login email
    setResetMessage(null);
    setResetError(null);
  };

  // Handle back to login
  const handleBackToLogin = () => {
    setShowReset(false);
    setResetMessage(null);
    setResetError(null);
  };

  // Handle password reset request
  const handlePasswordReset = async (e) => {
    e.preventDefault();

    const trimmedEmail = resetEmail.trim().toLowerCase();

    // Validate email format
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      setResetError('Please enter a valid email address');
      return;
    }

    setResetLoading(true);
    setResetError(null);
    setResetMessage(null);

    try {
      // Call self-reset-password edge function which confirms the user's email
      // before sending the reset (fixes silent failures for unconfirmed emails)
      const res = await fetch(`${SUPABASE_URL}/functions/v1/self-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.error || `Request failed (${res.status})`;
        if (msg.toLowerCase().includes('rate limit')) {
          setResetError('Email rate limit exceeded. Please wait a bit and try again, or ask an admin to reset your password.');
        } else {
          setResetError(msg);
        }
        return;
      }

      // Success - always show generic message (don't reveal if email exists)
      setResetMessage('If an account exists for this email, a password reset link has been sent.');
    } catch (err) {
      setResetError(err.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  // Shared inline styles for BRIO compliance
  const styles = {
    page: {
      minHeight: '100vh',
      background: 'var(--brio-white, #fff)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--brio-space-m)',
      fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
    },
    card: {
      background: 'var(--brio-white, #fff)',
      border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
      padding: 'var(--brio-space-l)',
      borderRadius: 0,
    },
    heading: {
      fontFamily: 'var(--brio-font-family)',
      fontWeight: 400,
      fontSize: '15px',
      color: 'var(--brio-black, #111)',
      marginBottom: 'var(--brio-space-s)',
    },
    label: {
      display: 'block',
      fontSize: '11px',
      fontWeight: 400,
      color: 'var(--brio-grey, #666)',
      marginBottom: '4px',
      fontFamily: 'var(--brio-font-family)',
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
      borderRadius: 0,
      fontFamily: 'var(--brio-font-family)',
      fontSize: '13px',
      color: 'var(--brio-black, #111)',
      background: 'var(--brio-white, #fff)',
      outline: 'none',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
      appearance: 'none',
    },
    primaryBtn: {
      width: '100%',
      padding: '10px 16px',
      background: 'var(--brio-gold, #9F902A)',
      color: 'var(--brio-white, #fff)',
      border: 'none',
      borderRadius: 0,
      fontFamily: 'var(--brio-font-family)',
      fontSize: '13px',
      fontWeight: 400,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background 0.15s ease',
    },
    secondaryBtn: {
      width: '100%',
      padding: '10px 16px',
      background: 'var(--brio-grey-light, #f5f5f5)',
      color: 'var(--brio-black, #111)',
      border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
      borderRadius: 0,
      fontFamily: 'var(--brio-font-family)',
      fontSize: '13px',
      fontWeight: 400,
      cursor: 'pointer',
      transition: 'background 0.15s ease',
    },
    textLink: {
      background: 'none',
      border: 'none',
      color: 'var(--brio-gold, #9F902A)',
      fontSize: '11px',
      fontFamily: 'var(--brio-font-family)',
      cursor: 'pointer',
      padding: 0,
      textDecoration: 'none',
    },
    errorBox: {
      marginBottom: 'var(--brio-space-s)',
      padding: '12px',
      background: 'var(--brio-red-light, rgba(147,73,72,0.1))',
      border: '1px solid var(--brio-red, #934948)',
      color: 'var(--brio-red, #934948)',
      fontSize: '11px',
      fontFamily: 'var(--brio-font-family)',
      borderRadius: 0,
    },
    successBox: {
      marginBottom: 'var(--brio-space-s)',
      padding: '12px',
      background: 'var(--brio-green-light, rgba(132,164,140,0.15))',
      border: '1px solid var(--brio-green, #84A48C)',
      color: '#3d6b4a',
      fontSize: '11px',
      fontFamily: 'var(--brio-font-family)',
      borderRadius: 0,
    },
    muted: {
      fontSize: '11px',
      color: 'var(--brio-grey, #666)',
      fontFamily: 'var(--brio-font-family)',
      fontWeight: 200,
    },
    fieldGroup: {
      marginBottom: 'var(--brio-space-s)',
    },
  };

  return (
    <div style={styles.page}>
      <div style={{ width: '100%', maxWidth: '384px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--brio-space-l)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: 'var(--brio-space-s)' }}>
            <BrioMark size={36} style={{ color: 'var(--brio-gold, #9F902A)' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 400, color: 'var(--brio-black, #111)', fontFamily: 'var(--brio-font-family)', margin: 0 }}>BRIO Atlas</h1>
          </div>
          <p style={styles.muted}>Sign in to access your timetracker</p>
        </div>

        {/* Login Card */}
        <div style={styles.card}>
          {magicLinkSent ? (
            <div style={{ textAlign: 'center', padding: 'var(--brio-space-s) 0' }}>
              <div style={{ fontSize: '32px', marginBottom: 'var(--brio-space-s)' }}>📧</div>
              <h2 style={styles.heading}>Check your email</h2>
              <p style={{ ...styles.muted, marginBottom: 'var(--brio-space-s)' }}>
                We sent a magic link to <strong style={{ fontWeight: 400 }}>{email}</strong>
              </p>
              <p style={{ ...styles.muted, fontSize: '10px' }}>
                Click the link in the email to sign in.
              </p>
              <button
                onClick={() => setMagicLinkSent(false)}
                style={{ ...styles.textLink, marginTop: 'var(--brio-space-s)' }}
              >
                ← Back to login
              </button>
            </div>
          ) : showReset ? (
            /* Forgot Password UI (inline) */
            <div>
              <h2 style={styles.heading}>Reset your password</h2>

              {/* Reset Success Message */}
              {resetMessage && (
                <div style={styles.successBox}>
                  ✓ {resetMessage}
                </div>
              )}

              {/* Reset Error Display */}
              {resetError && (
                <div style={styles.errorBox}>
                  {resetError}
                </div>
              )}

              <form onSubmit={handlePasswordReset}>
                {/* Email Input */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    disabled={resetLoading}
                    style={{
                      ...styles.input,
                      ...(resetLoading ? { background: 'var(--brio-grey-light)', cursor: 'not-allowed' } : {}),
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--brio-gold)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--brio-border)'; }}
                  />
                </div>

                {/* Send Reset Link Button */}
                <button
                  type="submit"
                  disabled={resetLoading}
                  style={{
                    ...styles.primaryBtn,
                    ...(resetLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                  }}
                  onMouseEnter={(e) => { if (!resetLoading) e.target.style.background = 'var(--brio-gold-hover)'; }}
                  onMouseLeave={(e) => { e.target.style.background = 'var(--brio-gold)'; }}
                >
                  {resetLoading ? (
                    <>
                      <svg className="animate-spin" style={{ width: 16, height: 16 }} viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
              </form>

              {/* Back to Login */}
              <div style={{ textAlign: 'center', marginTop: 'var(--brio-space-s)' }}>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  style={styles.textLink}
                >
                  ← Back to login
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSignIn}>
              {/* Error Display */}
              {error && (
                <div style={styles.errorBox}>
                  {error}
                </div>
              )}

              {/* Email Input */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                  style={{
                    ...styles.input,
                    ...(loading ? { background: 'var(--brio-grey-light)', cursor: 'not-allowed' } : {}),
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--brio-gold)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--brio-border)'; }}
                />
              </div>

              {/* Password Input */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    style={{
                      ...styles.input,
                      paddingRight: '40px',
                      ...(loading ? { background: 'var(--brio-grey-light)', cursor: 'not-allowed' } : {}),
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--brio-gold)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--brio-border)'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    style={{
                      position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                      color: 'var(--brio-grey, #999)', display: 'flex', alignItems: 'center',
                    }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div style={{ marginBottom: 'var(--brio-space-m)', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={handleShowReset}
                  style={styles.textLink}
                >
                  Forgot password?
                </button>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.primaryBtn,
                  ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
                onMouseEnter={(e) => { if (!loading) e.target.style.background = 'var(--brio-gold-hover)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'var(--brio-gold)'; }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" style={{ width: 16, height: 16 }} viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>

              {/* Divider */}
              <div style={{ position: 'relative', margin: 'var(--brio-space-m) 0' }}>
                <div style={{ borderTop: '1px solid var(--brio-border)', width: '100%' }} />
                <div style={{ position: 'absolute', top: '-9px', left: '50%', transform: 'translateX(-50%)', padding: '0 8px', background: 'var(--brio-white)', fontSize: '11px', color: 'var(--brio-grey)' }}>or</div>
              </div>

              {/* Magic Link Button */}
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={loading}
                style={{
                  ...styles.secondaryBtn,
                  ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
                onMouseEnter={(e) => { if (!loading) e.target.style.background = 'var(--brio-border)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'var(--brio-grey-light)'; }}
              >
                Send magic link
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p style={{ textAlign: 'center', fontSize: '9px', color: 'var(--brio-grey)', marginTop: 'var(--brio-space-m)', fontFamily: 'var(--brio-font-family)', fontWeight: 200 }}>
          Powered by Supabase Auth with Row Level Security
        </p>
      </div>
    </div>
  );
}
